import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface LeaveRequest {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: string;
  requestDate: string;
  approvedBy?: string;
  notes?: string;
  substitute?: string;
}

const LeaveRequests = () => {
  const routes = all_routes;
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [newRequest, setNewRequest] = useState<LeaveRequest>({
    id: "",
    type: "",
    startDate: "",
    endDate: "",
    reason: "",
    status: "pending",
    requestDate: new Date().toISOString().split("T")[0],
    substitute: "",
    notes: "",
  });

  const leaveTypes = [
    {
      value: "sick",
      label: "Sick Leave",
      icon: "ti-heart-plus",
      color: "danger",
      maxDays: 12,
    },
    {
      value: "personal",
      label: "Personal Leave",
      icon: "ti-user",
      color: "primary",
      maxDays: 7,
    },
    {
      value: "emergency",
      label: "Emergency Leave",
      icon: "ti-alert-triangle",
      color: "warning",
      maxDays: 3,
    },
    {
      value: "vacation",
      label: "Vacation Leave",
      icon: "ti-plane",
      color: "success",
      maxDays: 21,
    },
    {
      value: "maternity",
      label: "Maternity Leave",
      icon: "ti-baby-carriage",
      color: "info",
      maxDays: 90,
    },
    {
      value: "professional",
      label: "Professional Development",
      icon: "ti-school",
      color: "secondary",
      maxDays: 5,
    },
    {
      value: "bereavement",
      label: "Bereavement Leave",
      icon: "ti-heart-broken",
      color: "dark",
      maxDays: 5,
    },
  ];

  // Mock existing requests
  const existingRequests: LeaveRequest[] = [
    {
      id: "1",
      type: "sick",
      startDate: "2024-08-10",
      endDate: "2024-08-12",
      reason: "Flu symptoms and fever",
      status: "approved",
      requestDate: "2024-08-08",
      approvedBy: "Dr. Smith (Principal)",
      substitute: "Ms. Johnson",
    },
    {
      id: "2",
      type: "personal",
      startDate: "2024-08-15",
      endDate: "2024-08-15",
      reason: "Family appointment",
      status: "pending",
      requestDate: "2024-08-05",
      substitute: "Mr. Wilson",
    },
    {
      id: "3",
      type: "vacation",
      startDate: "2024-07-20",
      endDate: "2024-07-25",
      reason: "Summer vacation with family",
      status: "rejected",
      requestDate: "2024-07-01",
      notes: "Too many teachers on leave during this period",
    },
  ];

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case "approved":
        return "ti-check-circle";
      case "pending":
        return "ti-clock";
      case "rejected":
        return "ti-x-circle";
      default:
        return "ti-file";
    }
  };

  const calculateDays = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return (
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1
    );
  };

  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !newRequest.type ||
      !newRequest.startDate ||
      !newRequest.endDate ||
      !newRequest.reason
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    const days = calculateDays(newRequest.startDate, newRequest.endDate);
    const leaveType = leaveTypes.find((t) => t.value === newRequest.type);

    if (leaveType && days > leaveType.maxDays) {
      alert(`${leaveType.label} cannot exceed ${leaveType.maxDays} days.`);
      return;
    }

    console.log("Submitting leave request:", newRequest);
    setShowRequestModal(false);

    // Reset form
    setNewRequest({
      id: "",
      type: "",
      startDate: "",
      endDate: "",
      reason: "",
      status: "pending",
      requestDate: new Date().toISOString().split("T")[0],
      substitute: "",
      notes: "",
    });
  };

  const handleInputChange = (field: keyof LeaveRequest, value: string) => {
    setNewRequest((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Leave Requests</h3>
              <p className="text-muted mb-0">
                Request time off and manage leave applications
              </p>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-primary"
                onClick={() => setShowRequestModal(true)}
              >
                <i className="ti ti-plus me-2"></i>New Request
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <h4 className="text-warning">
                    {
                      existingRequests.filter((r) => r.status === "pending")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Pending</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <h4 className="text-success">
                    {
                      existingRequests.filter((r) => r.status === "approved")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Approved</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-danger-light">
                <div className="card-body text-center">
                  <h4 className="text-danger">
                    {
                      existingRequests.filter((r) => r.status === "rejected")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Rejected</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <h4 className="text-info">
                    {existingRequests
                      .filter((r) => r.status === "approved")
                      .reduce(
                        (sum, r) => sum + calculateDays(r.startDate, r.endDate),
                        0
                      )}
                  </h4>
                  <p className="mb-0">Days Used</p>
                </div>
              </div>
            </div>
          </div>

          {/* Leave Balance */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="card-title mb-0">Leave Balance</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {leaveTypes.map((leave) => (
                  <div key={leave.value} className="col-lg-3 col-md-6">
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <div
                        className={`avatar avatar-sm bg-${leave.color}-transparent me-3`}
                      >
                        <i
                          className={`ti ${leave.icon} text-${leave.color}`}
                        ></i>
                      </div>
                      <div>
                        <h6 className="mb-1">{leave.label}</h6>
                        <p className="mb-0 small text-muted">
                          {leave.maxDays} days available
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Requests List */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">My Leave Requests</h5>
            </div>
            <div className="card-body">
              {existingRequests.length > 0 ? (
                <div className="row g-3">
                  {existingRequests.map((request) => (
                    <div key={request.id} className="col-lg-6">
                      <div className="card border">
                        <div className="card-body">
                          <div className="d-flex align-items-start justify-content-between mb-3">
                            <div className="d-flex align-items-center">
                              <div
                                className={`avatar avatar-sm bg-${getStatusColor(
                                  request.status
                                )}-transparent me-3`}
                              >
                                <i
                                  className={`ti ${getStatusIcon(
                                    request.status
                                  )} text-${getStatusColor(request.status)}`}
                                ></i>
                              </div>
                              <div>
                                <h6 className="mb-1">
                                  {
                                    leaveTypes.find(
                                      (t) => t.value === request.type
                                    )?.label
                                  }
                                </h6>
                                <p className="text-muted mb-0 small">
                                  {calculateDays(
                                    request.startDate,
                                    request.endDate
                                  )}{" "}
                                  day(s)
                                </p>
                              </div>
                            </div>
                            <span
                              className={`badge bg-${getStatusColor(
                                request.status
                              )}`}
                            >
                              {request.status}
                            </span>
                          </div>

                          <div className="mb-3">
                            <div className="row text-center">
                              <div className="col-6">
                                <small className="text-muted">From</small>
                                <p className="mb-0 fw-semibold">
                                  {new Date(
                                    request.startDate
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">To</small>
                                <p className="mb-0 fw-semibold">
                                  {new Date(
                                    request.endDate
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="alert alert-light py-2 mb-3">
                            <small>
                              <strong>Reason:</strong> {request.reason}
                            </small>
                          </div>

                          {request.substitute && (
                            <p className="mb-2 small">
                              <strong>Substitute:</strong> {request.substitute}
                            </p>
                          )}

                          {request.approvedBy && (
                            <p className="mb-2 small text-success">
                              <strong>Approved by:</strong> {request.approvedBy}
                            </p>
                          )}

                          {request.notes && (
                            <div className="alert alert-warning py-2">
                              <small>
                                <strong>Note:</strong> {request.notes}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                    <i className="ti ti-calendar-off fs-1 text-secondary"></i>
                  </div>
                  <h5>No leave requests</h5>
                  <p className="text-muted">
                    You haven't submitted any leave requests yet.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowRequestModal(true)}
                  >
                    Create First Request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Request Modal */}
      {showRequestModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Leave Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRequestModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmitRequest}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Leave Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={newRequest.type}
                        onChange={(e) =>
                          handleInputChange("type", e.target.value)
                        }
                        required
                      >
                        <option value="">Select leave type...</option>
                        {leaveTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label} (Max: {type.maxDays} days)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Request Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={newRequest.requestDate}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Start Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={newRequest.startDate}
                        onChange={(e) =>
                          handleInputChange("startDate", e.target.value)
                        }
                        min={new Date().toISOString().split("T")[0]}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        End Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={newRequest.endDate}
                        onChange={(e) =>
                          handleInputChange("endDate", e.target.value)
                        }
                        min={
                          newRequest.startDate ||
                          new Date().toISOString().split("T")[0]
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Reason <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Please provide the reason for your leave..."
                        value={newRequest.reason}
                        onChange={(e) =>
                          handleInputChange("reason", e.target.value)
                        }
                        required
                      ></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Suggested Substitute Teacher
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name of teacher who can cover your classes"
                        value={newRequest.substitute}
                        onChange={(e) =>
                          handleInputChange("substitute", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Additional Notes</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Any additional information..."
                        value={newRequest.notes}
                        onChange={(e) =>
                          handleInputChange("notes", e.target.value)
                        }
                      ></textarea>
                    </div>
                    {newRequest.startDate && newRequest.endDate && (
                      <div className="col-12">
                        <div className="alert alert-info">
                          <strong>Duration:</strong>{" "}
                          {calculateDays(
                            newRequest.startDate,
                            newRequest.endDate
                          )}{" "}
                          day(s)
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowRequestModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="ti ti-send me-2"></i>Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeaveRequests;
